import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { getDespesasDepartamentosOmie, getDespesasLancamentos } from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import { limitesDoMes } from "../../components/gestaoRealizado/SincronizacaoDespesas";
import {
  ANO_ATUAL, AvisoErro, Badge, BarraFiltros, Card, botaoPequeno, Carregando, GradeKpis, Kpi, MESES, Seletor, Vazio, estiloTabela,
  formatCompacto, formatData, formatValor, inputStyle, opcoesAno, opcoesEmpresa, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Lançamentos do Contas a Pagar do Omie (Fase 2) com o rateio categoria × departamento. Abre
// filtrada a partir da célula da tela Despesas (?ano&mes&empresa&item&pai&departamento). Os filtros
// de item (por prefixo: "2.3" traz todo o 2.3.xx), categoria pai e departamento valem para a mesma
// linha de rateio; "No filtro" é a parte do título que
// cai nele (a soma bate com a célula da visão geral). Sem ?mes abre no mês atual; o ano inteiro
// é ?mes=todos.

const SEM = "__sem__";
const TODOS = "todos";
const POR_PAGINA = [25, 50, 100, 200];

const corStatus = (C, s) => ({ PAGO: C.success, CANCELADO: C.danger, ATRASADO: C.danger }[s] || C.warning);

export default function DespesasLancamentosRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const [params, setParams] = useSearchParams();
  const ano = Number(params.get("ano") || ANO_ATUAL);
  const mesParam = params.get("mes") || String(new Date().getMonth() + 1);
  const mes = mesParam === TODOS ? "" : mesParam;
  const empresa = params.get("empresa") || "";
  const item = params.get("item") || "";
  const pai = params.get("pai") || "";
  const departamento = params.get("departamento") || "";
  const cancelados = params.get("cancelados") === "1";
  const [itemDigitado, setItemDigitado] = useState(item === SEM ? "" : item);
  const [busca, setBusca] = useState("");
  const [expandidos, setExpandidos] = useState(() => new Set());
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const T = estiloTabela(C);

  const mudar = (chave, valor) => {
    const p = new URLSearchParams(params);
    if (valor === "" || valor === null || valor === false) p.delete(chave); else p.set(chave, valor);
    setParams(p, { replace: true });
  };

  const [de, ate] = mes
    ? limitesDoMes(`${ano}-${String(mes).padStart(2, "0")}`)
    : [`${ano}-01-01`, `${ano}-12-31`];
  const { dados, carregando, erro } = useCarregar(() => getDespesasLancamentos({
    de, ate, empresa,
    codigo_item: item && item !== SEM ? item : "", sem_codigo: item === SEM ? "true" : "", categoria_pai: pai,
    departamento: departamento && departamento !== SEM ? departamento : "", sem_departamento: departamento === SEM ? "true" : "",
    incluir_cancelados: cancelados ? "true" : "",
  }), [de, ate, empresa, item, pai, departamento, cancelados]);
  const opcoesDep = useCarregar(() => getDespesasDepartamentosOmie({}), []);

  const filtrando = Boolean(item || pai || departamento);
  // guarda as opções da última resposta para o seletor não esvaziar enquanto recarrega
  const [opcoesPai, setOpcoesPai] = useState([]);
  useEffect(() => { if (dados?.opcoesPai) setOpcoesPai(dados.opcoesPai); }, [dados]);
  const lancamentos = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return dados?.lancamentos || [];
    return (dados?.lancamentos || []).filter((l) => [
      l.fornecedorNome, l.fornecedorCnpj, l.numeroDocumento, l.codigoTitulo, ...l.rateio.map((r) => `${r.codigoCategoria} ${r.descricaoCategoria || ""} ${r.departamentoOmie || ""}`),
    ].join(" ").toLowerCase().includes(termo));
  }, [dados, busca]);
  // volta para a primeira página sempre que o conjunto filtrado muda
  useEffect(() => { setPagina(1); }, [lancamentos, porPagina]);
  const totalPaginas = Math.max(1, Math.ceil(lancamentos.length / porPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const visiveis = lancamentos.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina);
  const soma = (campo) => lancamentos.reduce((a, l) => a + (l[campo] || 0), 0);
  const pagos = lancamentos.filter((l) => l.status === "PAGO");

  // uma linha por linha de rateio (título repetido), com todos os lançamentos filtrados, não só a página
  const exportarExcel = () => {
    const linhas = lancamentos.flatMap((l) => (l.rateio.length ? l.rateio : [{}]).map((r) => ({
      Empresa: l.empresa,
      "Título Omie": l.codigoTitulo,
      Emissão: formatData(l.dataEmissao),
      Vencimento: formatData(l.dataVencimento),
      Pagamento: formatData(l.dataPagamento),
      Status: l.status || "",
      Removido: l.removidoOmie ? "sim" : "",
      Fornecedor: l.fornecedorNome || "",
      CNPJ: l.fornecedorCnpj || "",
      Documento: l.numeroDocumento || "",
      Parcela: l.parcela || "",
      Origem: l.origem || "",
      "Valor do título": l.valor,
      "Valor pago": l.valorPago,
      "Categoria Omie": r.codigoCategoria || "",
      "Descrição categoria": r.descricaoCategoria || "",
      Item: r.codigoItem || "",
      "Departamento Omie": r.departamentoOmie || "",
      Departamento: r.departamento || "",
      "% rateio": r.percentual,
      "Valor rateio": r.valor,
      ...(filtrando && { "No filtro": r.noFiltro ? "sim" : "não" }),
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas), "Lançamentos");
    XLSX.writeFile(wb, `lancamentos_despesas_${ano}${mes ? `_${String(mes).padStart(2, "0")}` : ""}${empresa ? `_${empresa}` : ""}.xlsx`);
  };

  const alternar = (id) => setExpandidos((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const rotuloCampo = { display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" };
  const campo = { ...inputStyle(C), minHeight: 36, fontSize: 13, padding: "6px 10px" };

  return (
    <RealizadoLayout titulo="Lançamentos de despesas" isLight={isLight} setIsLight={setIsLight}>
      <BarraFiltros>
        <Seletor C={C} rotulo="Ano da emissão" value={ano} onChange={(v) => mudar("ano", v)} opcoes={opcoesAno} />
        <Seletor C={C} rotulo="Mês" value={mesParam} onChange={(v) => mudar("mes", v)} opcoes={[
          { value: TODOS, label: "Ano todo" }, ...MESES.map((m, i) => ({ value: String(i + 1), label: m })),
        ]} />
        <Seletor C={C} rotulo="Empresa" value={empresa} onChange={(v) => mudar("empresa", v)} opcoes={opcoesEmpresa} />
        <Seletor C={C} rotulo="Categoria pai" value={pai} onChange={(v) => mudar("pai", v)} opcoes={[
          { value: "", label: "Todas" },
          ...opcoesPai.map((p) => ({ value: p.codigo || p.nome, label: p.codigo ? `${p.codigo} ${p.nome || ""}` : p.nome })),
        ]} />
        <label style={rotuloCampo}>
          Item do plano
          <div style={{ display: "flex", gap: 6 }}>
            <input
              value={item === SEM ? "" : itemDigitado} placeholder={item === SEM ? "sem código" : "ex.: 2.3 ou 2.3.01"} style={{ ...campo, width: 120 }}
              onChange={(e) => setItemDigitado(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") mudar("item", itemDigitado.trim()); }}
              onBlur={() => { if (item !== SEM && itemDigitado.trim() !== item) mudar("item", itemDigitado.trim()); }}
            />
            <select value={item === SEM ? SEM : ""} onChange={(e) => { setItemDigitado(""); mudar("item", e.target.value); }} style={{ ...campo, width: 130 }} aria-label="Itens sem código">
              <option value="">Qualquer</option>
              <option value={SEM}>Sem código</option>
            </select>
          </div>
        </label>
        <Seletor C={C} rotulo="Departamento" value={departamento} onChange={(v) => mudar("departamento", v)} opcoes={[
          { value: "", label: "Todos" },
          ...(opcoesDep.dados?.opcoes || []).map((d) => ({ value: d, label: d })),
          { value: SEM, label: "Sem departamento" },
        ]} />
        <Seletor C={C} rotulo="Cancelados" value={cancelados ? "1" : ""} onChange={(v) => mudar("cancelados", v)} opcoes={[
          { value: "", label: "Não mostrar" }, { value: "1", label: "Mostrar" },
        ]} />
        <label style={rotuloCampo}>
          Buscar
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Fornecedor, CNPJ, documento, categoria" style={{ ...campo, width: 260 }} />
        </label>
      </BarraFiltros>
      <AvisoErro C={C}>{erro}</AvisoErro>

      {carregando && !dados ? <Carregando C={C} /> : dados && (
        <>
          <GradeKpis>
            <Kpi C={C} rotulo="Títulos" valor={lancamentos.length} detalhe={dados.total > dados.limite ? `Mostrando ${dados.limite} de ${dados.total} — refine o filtro` : `${mes ? MESES[mes - 1] : "Ano"} ${ano}`} />
            <Kpi C={C} rotulo={filtrando ? "Valor no filtro" : "Valor dos títulos"} valor={formatCompacto(soma(filtrando ? "valorFiltro" : "valor"))} detalhe={filtrando ? `de ${formatCompacto(soma("valor"))} nos títulos inteiros` : "Soma do valor dos títulos"} />
            <Kpi C={C} rotulo="Pagos" valor={pagos.length} detalhe={formatCompacto(pagos.reduce((a, l) => a + l.valor, 0))} cor={C.success} />
            <Kpi C={C} rotulo="A pagar" valor={lancamentos.length - pagos.length} detalhe={formatCompacto(soma("valor") - pagos.reduce((a, l) => a + l.valor, 0))} cor={C.warning} />
          </GradeKpis>

          <Card
            C={C} titulo={`${lancamentos.length} lançamento(s)`} subtitulo="Contas a Pagar do Omie por data de emissão. Clique na seta para ver o rateio (categoria × departamento)."
            acoes={<button onClick={exportarExcel} disabled={!lancamentos.length} style={{ ...botaoPequeno(C.success, true), display: "inline-flex", alignItems: "center", gap: 6 }}><Download size={14} /> Exportar Excel</button>}
          >
            {lancamentos.length === 0 ? <Vazio C={C}>Nenhum lançamento com esses filtros.</Vazio> : (
              <div style={{ ...T.wrap, maxHeight: "65vh" }}>
                <table style={{ ...T.table, minWidth: 1200 }}>
                  <thead><tr>
                    <th style={T.th} />
                    <th style={T.th}>Empresa</th>
                    <th style={T.th}>Emissão</th>
                    <th style={T.th}>Vencimento</th>
                    <th style={T.th}>Pagamento</th>
                    <th style={T.th}>Status</th>
                    <th style={T.th}>Fornecedor</th>
                    <th style={T.th}>Documento</th>
                    <th style={T.th}>Categoria · item</th>
                    <th style={T.th}>Departamento</th>
                    <th style={{ ...T.th, ...T.thNum }}>Valor</th>
                    {filtrando && <th style={{ ...T.th, ...T.thNum }}>No filtro</th>}
                  </tr></thead>
                  <tbody>
                    {visiveis.map((l) => {
                      const r0 = l.rateio[0] || {};
                      const varios = l.rateio.length > 1;
                      return (
                        <Fragment key={l.id}>
                          <tr style={l.removidoOmie ? { opacity: 0.5 } : undefined}>
                            <td style={{ ...T.td, cursor: "pointer", color: C.muted }} onClick={() => alternar(l.id)} title="Ver rateio">
                              {expandidos.has(l.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </td>
                            <td style={T.td}>{l.empresa}</td>
                            <td style={{ ...T.td, whiteSpace: "nowrap" }}>{formatData(l.dataEmissao)}</td>
                            <td style={{ ...T.td, whiteSpace: "nowrap" }}>{formatData(l.dataVencimento)}</td>
                            <td style={{ ...T.td, whiteSpace: "nowrap" }}>{formatData(l.dataPagamento)}</td>
                            <td style={T.td}>
                              <Badge cor={corStatus(C, l.status)}>{l.status || "—"}</Badge>
                              {l.removidoOmie && <> <Badge cor={C.danger} title="Sumiu do Omie na última sincronização">removido</Badge></>}
                              {l.valor < 0 && <> <Badge cor={C.success} title="Entrada na conta corrente em categoria de despesa: abate o item">estorno</Badge></>}
                            </td>
                            <td style={{ ...T.td, maxWidth: 260 }}>
                              {l.fornecedorNome || "—"}
                              {l.fornecedorCnpj && <div style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>{l.fornecedorCnpj}</div>}
                            </td>
                            <td style={{ ...T.td, maxWidth: 160 }}>{l.numeroDocumento || "—"}{l.parcela && <span style={{ color: C.muted }}> · {l.parcela}</span>}</td>
                            <td style={{ ...T.td, maxWidth: 280 }}>
                              {varios ? <span style={{ color: C.muted }}>Rateado em {l.rateio.length} linhas</span> : (
                                <>
                                  {r0.codigoItem ? <b>{r0.codigoItem}</b> : <Badge cor={C.warning}>sem código</Badge>}{" "}
                                  <span style={{ color: C.muted }}>{r0.descricaoCategoria || r0.codigoCategoria}</span>
                                </>
                              )}
                            </td>
                            <td style={{ ...T.td, maxWidth: 220 }}>
                              {varios ? "—" : r0.departamento || <Badge cor={C.warning} title={r0.departamentoOmie ? `Omie: ${r0.departamentoOmie} (sem de-para)` : "Sem departamento no Omie"}>sem departamento</Badge>}
                            </td>
                            <td style={{ ...T.td, ...T.tdNum, fontWeight: 700 }}>{formatValor(l.valor)}</td>
                            {filtrando && <td style={{ ...T.td, ...T.tdNum }}>{formatValor(l.valorFiltro)}</td>}
                          </tr>
                          {expandidos.has(l.id) && (
                            <tr>
                              <td />
                              <td colSpan={filtrando ? 11 : 10} style={{ ...T.td, background: C.bg }}>
                                <table style={{ ...T.table, fontSize: 11 }}>
                                  <thead><tr>
                                    <th style={T.th}>Categoria no Omie</th>
                                    <th style={T.th}>Item</th>
                                    <th style={T.th}>Departamento no Omie</th>
                                    <th style={T.th}>Departamento (plataforma)</th>
                                    <th style={{ ...T.th, ...T.thNum }}>%</th>
                                    <th style={{ ...T.th, ...T.thNum }}>Valor</th>
                                  </tr></thead>
                                  <tbody>
                                    {l.rateio.map((r, i) => (
                                      <tr key={i} style={filtrando && !r.noFiltro ? { opacity: 0.45 } : undefined}>
                                        <td style={T.td}>{r.codigoCategoria} <span style={{ color: C.muted }}>{r.descricaoCategoria || ""}</span></td>
                                        <td style={T.td}>{r.codigoItem || <Badge cor={C.warning}>sem código</Badge>}</td>
                                        <td style={T.td}>{r.departamentoOmie || "—"}</td>
                                        <td style={T.td}>{r.departamento || <Badge cor={C.warning}>{r.departamentoOmie ? "sem de-para" : "sem departamento"}</Badge>}</td>
                                        <td style={{ ...T.td, ...T.tdNum }}>{r.percentual.toFixed(2)}</td>
                                        <td style={{ ...T.td, ...T.tdNum }}>{formatValor(r.valor)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                                  {l.codigoTitulo.startsWith("CC-") ? `Lançamento avulso da conta corrente nº ${l.codigoTitulo.slice(3)} (sem título; data = pagamento)` : `Título Omie nº ${l.codigoTitulo}`} · origem {l.origem || "—"}{l.valorPago !== null && ` · pago ${formatValor(l.valorPago)}`}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {lancamentos.length > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 12, fontSize: 12, color: C.muted }}>
                <span>
                  {(paginaAtual - 1) * porPagina + 1}–{Math.min(paginaAtual * porPagina, lancamentos.length)} de {lancamentos.length}
                  {" · "}
                  <select value={porPagina} onChange={(e) => setPorPagina(Number(e.target.value))} style={{ ...campo, minHeight: 30, padding: "2px 6px", width: "auto" }} aria-label="Lançamentos por página">
                    {POR_PAGINA.map((n) => <option key={n} value={n}>{n} por página</option>)}
                  </select>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <button onClick={() => setPagina(1)} disabled={paginaAtual === 1} style={botaoPequeno(C.brand, true)}>Primeira</button>
                  <button onClick={() => setPagina(paginaAtual - 1)} disabled={paginaAtual === 1} style={botaoPequeno(C.brand, true)} aria-label="Página anterior"><ChevronLeft size={14} /></button>
                  <span>Página {paginaAtual} de {totalPaginas}</span>
                  <button onClick={() => setPagina(paginaAtual + 1)} disabled={paginaAtual === totalPaginas} style={botaoPequeno(C.brand, true)} aria-label="Próxima página"><ChevronRight size={14} /></button>
                  <button onClick={() => setPagina(totalPaginas)} disabled={paginaAtual === totalPaginas} style={botaoPequeno(C.brand, true)}>Última</button>
                </span>
              </div>
            )}
          </Card>
        </>
      )}
    </RealizadoLayout>
  );
}
